<script setup lang="ts">
import { computed, ref } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import { type Patient } from '@/api/patients';
import { vitalSignsApi, type VitalSign } from '@/api/vital-signs';
import { vitalSignRecordsApi, type VitalSignRecord, type VitalSignRecordGroup } from '@/api/vital-sign-records';
import { clinicalAuthorName } from '@/utils/clinical-author';
import { useFileDownload } from '@/composables/useFileDownload';
import { useResponsiveTable } from '@/composables/useResponsiveTable';
import { Line } from 'vue-chartjs';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  type ChartData,
} from 'chart.js';
import GoBackButton from '@/components/go-back-button.vue';
import ClinicalRecordHistory from '@/components/clinical-records/clinical-record-history.vue';
import ShowVoidedSwitch from '@/components/clinical-records/show-voided-switch.vue';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
);

const props = defineProps<{
  patient: Patient;
}>();

const { downloadFile } = useFileDownload();
const { tableProps } = useResponsiveTable({ desktopItemsPerPage: 25 });

const showChartModal = ref(false);
// Anulados stay in the history, crossed out, when asked for; the latest
// readings and the charts leave them out.
const showVoided = ref(false);
const selectedVitalSign = ref<VitalSign | null>(null);
const startDate = ref('');
const endDate = ref('');
const downloadingPdf = ref(false);

const { data: vitalSigns } = useQuery({
  queryKey: ['vitalSigns'],
  queryFn: () => vitalSignsApi.index(),
});

const { data: latestVitalSignRecords } = useQuery({
  queryKey: ['latestVitalSignRecords', props.patient.id],
  queryFn: () => vitalSignRecordsApi.latestIndex(props.patient.id),
  initialData: [],
});

const { data: vitalSignRecords } = useQuery({
  queryKey: computed(() => [
    'vitalSignRecords',
    selectedVitalSign.value?.id,
    startDate.value,
    endDate.value,
  ]),
  queryFn: () => {
    if (!selectedVitalSign.value) throw new Error('No vital sign selected');

    return vitalSignRecordsApi.index(
      props.patient.id,
      selectedVitalSign.value.id,
      startDate.value || undefined,
      endDate.value || undefined,
    );
  },
  enabled: computed(() => !!selectedVitalSign.value),
  initialData: [],
});

const { data: groupedRecords, isFetching: isFetchingGrouped } = useQuery({
  queryKey: computed(() => ['groupedVitalSignRecords', props.patient.id, startDate.value, endDate.value]),
  queryFn: () => vitalSignRecordsApi.groupedIndex(
    props.patient.id,
    startDate.value || undefined,
    endDate.value || undefined,
  ),
  initialData: [],
});

const latestRecordsByVitalSign = computed(() => {
  const map = new Map<number, VitalSignRecord>();
  for (const record of latestVitalSignRecords.value) {
    map.set(record.vitalSignId, record);
  }

  return map;
});

function openChartModal(vitalSign: VitalSign) {
  selectedVitalSign.value = vitalSign;
  showChartModal.value = true;
}

async function downloadPdf() {
  downloadingPdf.value = true;
  try {
    const response = await vitalSignRecordsApi.indexAsPdf(
      props.patient.id,
      startDate.value || undefined,
      endDate.value || undefined,
    );
    downloadFile(response);
  } finally {
    downloadingPdf.value = false;
  }
}

const tableHeaders = computed(() => {
  const headers: Array<{ title: string; key: string; sortable: boolean }> = [
    { title: 'Fecha / Hora', key: 'humanRecordedAt', sortable: false },
  ];

  if (vitalSigns.value) {
    vitalSigns.value.forEach((vs: VitalSign) => {
      headers.push({
        title: `${vs.name} (${vs.unit})`,
        key: `vs_${vs.id}`,
        sortable: false,
      });
    });
  }
  headers.push({ title: 'Registrado por', key: 'authors', sortable: false });

  return headers;
});

// One row per reading time: each vital sign's value in its `vs_<id>` column,
// and the record behind it for its "Editado" mark and history, plus the
// anulado readings of that sign when they are shown.
type HistoryRow = {
  humanRecordedAt: string;
  records: Partial<Record<number, VitalSignRecord>>;
  voidedRecords: Partial<Record<number, VitalSignRecord[]>>;
  // Who recorded the readings of the row.
  authors: string;
} & Record<`vs_${number}`, string>;

const voidedCount = computed(
  () => (groupedRecords.value ?? []).flatMap(group => group.records).filter(record => record.voided).length,
);

function shownRecords(group: VitalSignRecordGroup): VitalSignRecord[] {
  return group.records.filter(record => showVoided.value || !record.voided);
}

const tableItems = computed<HistoryRow[]>(() => {
  if (!groupedRecords.value || !vitalSigns.value) return [];

  return groupedRecords.value.filter(group => shownRecords(group).length > 0).map((group: VitalSignRecordGroup) => {
    const row = {
      humanRecordedAt: group.humanRecordedAt, records: {}, voidedRecords: {}, authors: '--',
    } as HistoryRow;
    const records = shownRecords(group);

    vitalSigns.value!.forEach((vs: VitalSign) => {
      const record = records.find((r: VitalSignRecord) => r.vitalSignId === vs.id && !r.voided);
      const voided = records.filter((r: VitalSignRecord) => r.vitalSignId === vs.id && r.voided);
      row[`vs_${vs.id}`] = record ? String(record.formattedValue) : '--';
      if (record) row.records[vs.id] = record;
      if (voided.length > 0) row.voidedRecords[vs.id] = voided;
    });
    const authors = new Set(records.map((record) => clinicalAuthorName(record.author)).filter(Boolean));
    if (authors.size > 0) row.authors = [...authors].join(', ');

    return row;
  });
});

const chartData = computed<ChartData<'line'> | null>(() => {
  if (!selectedVitalSign.value) return null;

  type VitalSignRecordValue = number | { systolic: number; diastolic: number };
  const recordsInForce = vitalSignRecords.value.filter((record: VitalSignRecord) => !record.voided);
  const values: VitalSignRecordValue[] = recordsInForce.map((record: VitalSignRecord) => {
    if (selectedVitalSign.value?.code === 'blood_pressure') {
      return {
        systolic: record.bloodPressureRecordSystolic,
        diastolic: record.bloodPressureRecordDiastolic,
      };
    }

    return parseFloat(record.value);
  });

  const dates = recordsInForce.map((record: VitalSignRecord) => record.humanRecordedAt);

  if (selectedVitalSign.value.code === 'blood_pressure') {
    return {
      labels: dates,
      datasets: [
        {
          label: 'Sistólica',
          data: values.map(record => (record as { systolic: number }).systolic),
          borderColor: '#5333ED',
          tension: 0.1,
        },
        {
          label: 'Diastólica',
          data: values.map(record => (record as { diastolic: number }).diastolic),
          borderColor: '#FF6384',
          tension: 0.1,
        },
      ],
    };
  }

  return {
    labels: dates,
    datasets: [
      {
        label: selectedVitalSign.value.name,
        data: values as number[],
        borderColor: '#5333ED',
        tension: 0.1,
      },
    ],
  };
});

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: 'bottom' as const,
    },
    title: {
      display: false,
    },
  },
  scales: {
    y: {
      beginAtZero: true,
    },
  },
};
</script>
<template>
  <v-container class="v-col-12 v-col-md-10">
    <go-back-button :path="`/patients/${patient.id}`" />

    <v-card class="mt-4">
      <v-toolbar
        color="white"
        flat
      >
        <v-toolbar-title class="font-weight-bold">
          Signos Vitales de {{ patient.fullName }}
        </v-toolbar-title>
      </v-toolbar>
      <v-toolbar
        color="white"
        flat
      >
        <v-row
          class="align-center px-2"
          no-gutters
        >
          <v-col
            cols="12"
            sm="6"
            md="3"
            class="pa-2"
          >
            <v-text-field
              v-model="startDate"
              type="date"
              label="Desde"
              density="compact"
              variant="outlined"
              hide-details
              clearable
            />
          </v-col>
          <v-col
            cols="12"
            sm="6"
            md="3"
            class="pa-2"
          >
            <v-text-field
              v-model="endDate"
              type="date"
              label="Hasta"
              density="compact"
              variant="outlined"
              hide-details
              clearable
            />
          </v-col>
          <v-spacer />
          <v-col
            cols="auto"
            class="pa-2"
          >
            <v-btn
              color="primary"
              variant="outlined"
              :loading="downloadingPdf"
              prepend-icon="mdi-file-pdf-box"
              @click="downloadPdf"
            >
              Descargar PDF
            </v-btn>
          </v-col>
        </v-row>
      </v-toolbar>
      <v-card-text>
        <v-row>
          <v-col
            v-for="vitalSign in vitalSigns"
            :key="vitalSign.id"
            cols="12"
            sm="6"
            md="3"
          >
            <v-card class="h-100 vital-sign-card">
              <v-card-text class="pa-4">
                <div class="d-flex align-center mb-2">
                  <v-icon
                    size="20"
                    class="mr-2"
                    color="primary"
                  >
                    {{ `mdi-${vitalSign.icon}` }}
                  </v-icon>
                  <span class="text-body-2 text-medium-emphasis">{{ vitalSign.name }}</span>
                </div>
                <div class="d-flex align-center">
                  <div class="d-flex align-baseline">
                    <span
                      v-if="latestRecordsByVitalSign.get(vitalSign.id)"
                      class="text-h5 font-weight-bold text-primary"
                    >
                      {{ latestRecordsByVitalSign.get(vitalSign.id)!.formattedValue }}
                    </span>
                    <span
                      v-else
                      class="text-h5 text-grey-lighten-1"
                    >
                      --
                    </span>
                    <span class="ml-1 text-caption text-medium-emphasis">{{ vitalSign.unit }}</span>
                  </div>
                  <v-spacer />
                  <v-btn
                    v-if="latestRecordsByVitalSign.get(vitalSign.id)"
                    icon
                    variant="text"
                    size="small"
                    color="primary"
                    @click="openChartModal(vitalSign)"
                  >
                    <v-icon>mdi-chart-line</v-icon>
                  </v-btn>
                </div>
                <div
                  v-if="latestRecordsByVitalSign.get(vitalSign.id)"
                  class="text-caption text-medium-emphasis mt-1"
                >
                  {{ latestRecordsByVitalSign.get(vitalSign.id)!.humanRecordedAt }}
                  <div v-if="latestRecordsByVitalSign.get(vitalSign.id)!.author">
                    {{ clinicalAuthorName(latestRecordsByVitalSign.get(vitalSign.id)!.author) }}
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <v-data-table
          v-bind="tableProps"
          class="mt-8"
          :headers="tableHeaders"
          :items="tableItems"
          :loading="isFetchingGrouped"
          :header-props="{ class: 'font-weight-bold' }"
          no-data-text="No hay registros de signos vitales."
        >
          <!-- Only an edited reading carries the mark and its history here; every
               reading has one in its shift. -->
          <template
            v-for="vitalSign in vitalSigns ?? []"
            :key="vitalSign.id"
            #[`item.vs_${vitalSign.id}`]="{ item }"
          >
            <span class="text-no-wrap">{{ item[`vs_${vitalSign.id}`] }}</span>
            <clinical-record-history
              v-if="item.records[vitalSign.id]?.edited"
              item-type="VitalSignRecord"
              :item-id="item.records[vitalSign.id]!.id"
              edited
              :label="`${vitalSign.name} · ${item.humanRecordedAt}`"
            />
            <div
              v-for="voided in item.voidedRecords[vitalSign.id] ?? []"
              :key="voided.id"
              class="d-flex align-center ga-1 text-no-wrap"
            >
              <span class="text-decoration-line-through text-medium-emphasis">
                {{ voided.formattedValue }}
              </span>
              <v-chip
                size="x-small"
                variant="tonal"
                color="error"
                label
              >
                Anulado
              </v-chip>
              <!-- Who anuló it, when and why. -->
              <clinical-record-history
                item-type="VitalSignRecord"
                :item-id="voided.id"
                :edited="voided.edited"
                :label="`${vitalSign.name} anulado · ${item.humanRecordedAt}`"
              />
            </div>
          </template>
          <template #top>
            <v-toolbar
              color="white"
              flat
            >
              <v-toolbar-title class="font-weight-bold">
                Historial de Registros
              </v-toolbar-title>
              <show-voided-switch
                v-model="showVoided"
                :count="voidedCount"
                class="me-4"
              />
            </v-toolbar>
          </template>
        </v-data-table>

        <v-dialog
          v-model="showChartModal"
          max-width="800"
        >
          <v-card v-if="selectedVitalSign">
            <v-card-title class="d-flex justify-space-between align-center pa-4">
              <span>Historial de {{ selectedVitalSign.name }}</span>
              <v-btn
                icon
                variant="text"
                @click="showChartModal = false"
              >
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-card-title>
            <v-card-text>
              <Line
                v-if="chartData"
                :data="chartData"
                :options="chartOptions"
                :style="{ height: '400px' }"
              />
            </v-card-text>
          </v-card>
        </v-dialog>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<style scoped>
.vital-sign-card {
  transition: box-shadow .2s;
}
</style>
